import { useState, useRef, useEffect } from 'react';

interface OTPVerifyModalProps {
  orderId: string;
  customerName: string;
  onVerify: (otp: string) => Promise<void>;
  onClose: () => void;
  externalError?: string;
}

export default function OTPVerifyModal({ orderId, customerName, onVerify, onClose, externalError }: OTPVerifyModalProps) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const displayError = externalError || localError;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (idx: number, val: string) => {
    if (!/^\d*$/.test(val)) return;
    const newDigits = [...digits];
    newDigits[idx] = val.slice(-1);
    setDigits(newDigits);
    setLocalError('');
    if (val && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      setLocalError('');
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otp = digits.join('');
    if (otp.length < 6) {
      setLocalError('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    setLocalError('');
    await onVerify(otp);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden animate-bounce-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-700 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <i className="ri-shield-keyhole-fill text-white text-xl"></i>
          </div>
          <div>
            <p className="text-white font-bold text-base leading-none">Delivery OTP</p>
            <p className="text-white/70 text-xs mt-0.5">Ask customer for their 6-digit OTP</p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white"></i>
          </button>
        </div>

        <div className="p-5">
          {/* Customer info */}
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
              <i className="ri-user-3-line text-gray-600"></i>
            </div>
            <div>
              <p className="text-gray-900 font-semibold text-sm">{customerName}</p>
              <p className="text-gray-400 text-xs font-mono">#{orderId.toUpperCase()}</p>
            </div>
          </div>

          <p className="text-gray-600 text-sm text-center mb-4">
            Enter the <strong>6-digit OTP</strong> shared by the customer to confirm delivery
          </p>

          {/* OTP Input boxes */}
          <div className="flex gap-2 justify-center mb-4" onPaste={handlePaste}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={el => { inputRefs.current[idx] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleChange(idx, e.target.value)}
                onKeyDown={e => handleKeyDown(idx, e)}
                className={`w-11 h-14 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all ${
                  displayError
                    ? 'border-red-400 bg-red-50 text-red-700'
                    : d
                    ? 'border-gray-900 bg-gray-50 text-gray-900'
                    : 'border-gray-200 text-gray-400'
                } focus:border-gray-900`}
              />
            ))}
          </div>

          {displayError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
              <i className="ri-error-warning-fill text-red-500 shrink-0"></i>
              <p className="text-red-600 text-xs font-medium">{displayError}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || digits.join('').length < 6}
              className="flex-1 py-3 rounded-xl bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin"></i> Verifying...</>
              ) : (
                <><i className="ri-check-double-line"></i> Verify & Deliver</>
              )}
            </button>
          </div>

          <p className="text-center text-gray-400 text-xs mt-3">
            <i className="ri-information-line mr-1"></i>
            OTP was shown to customer when they placed the order
          </p>
        </div>
      </div>
    </div>
  );
}
